import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Conn } from '../conn';
import {
  listAgents, getAgentMarkdown, putAgentMarkdown, defineAgent, deleteAgent, runAgent,
} from '../api';

const TEMPLATES: { id: string; title: string; desc: string; requirements: string }[] = [
  { id: 'release-notes-writer', title: 'Release Notes Writer', desc: 'Turns merged PRs into clean, grouped release notes.', requirements: 'An agent that writes release notes from a list of merged pull requests, grouped by feature/fix/chore with a short summary line each.' },
  { id: 'sql-explainer', title: 'SQL Explainer', desc: 'Explains a SQL query in plain English, step by step.', requirements: 'An agent that takes a SQL query and explains what it does in plain English, step by step, and flags performance risks.' },
  { id: 'meeting-summarizer', title: 'Meeting Summarizer', desc: 'Summarizes a transcript into decisions + action items.', requirements: 'An agent that summarizes a meeting transcript into key decisions and a bulleted list of owner → action item.' },
  { id: 'calc', title: 'Calculator', desc: 'Evaluates arithmetic and returns just the number.', requirements: 'A calculator agent that evaluates arithmetic and replies with only the numeric result, no words.' },
];

export default function AgentsPage({ conn }: { conn: Conn }) {
  const [agents, setAgents] = useState<string[]>([]);
  const [tab, setTab] = useState<'mine' | 'templates'>('mine');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [md, setMd] = useState('');
  const [prompt, setPrompt] = useState('What is 7 times 8? Reply with only the number.');
  const [out, setOut] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!conn.apiKey) { setErr('Connect an API key (sidebar) to load agents.'); return; }
    setErr('');
    try { setAgents(await listAgents(conn.baseUrl, conn.apiKey)); }
    catch (e) { setErr(String(e)); }
  }, [conn.baseUrl, conn.apiKey]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => agents.filter((a) => a.toLowerCase().includes(q.toLowerCase())),
    [agents, q]);
  const filteredTpl = useMemo(
    () => TEMPLATES.filter((t) => (t.title + t.desc).toLowerCase().includes(q.toLowerCase())),
    [q]);

  async function open(id: string) {
    setSel(id); setOut(''); setErr(''); setBusy(true);
    try { setMd(await getAgentMarkdown(conn.baseUrl, conn.apiKey, id)); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function save() {
    if (!sel) return; setBusy(true); setErr('');
    try { await putAgentMarkdown(conn.baseUrl, conn.apiKey, sel, md); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function install(requirements: string, agentId?: string) {
    setBusy(true); setErr(''); setTab('mine');
    try {
      const r = await defineAgent(conn.baseUrl, conn.apiKey, requirements, { provider: conn.provider, model: conn.model || undefined });
      await load(); await open(r.agentId || agentId || '');
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function remove(id: string) {
    setBusy(true); setErr('');
    try { await deleteAgent(conn.baseUrl, conn.apiKey, id); if (sel === id) setSel(null); await load(); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  }
  async function run() {
    if (!sel) return; setBusy(true); setErr(''); setOut('running…');
    try {
      const r = await runAgent(conn.baseUrl, conn.apiKey, sel, prompt, { provider: conn.provider, model: conn.model || undefined });
      setOut(r.finalText || JSON.stringify(r.raw));
    } catch (e) { setOut(''); setErr(String(e)); } finally { setBusy(false); }
  }

  const [req, setReq] = useState('');

  return (
    <div className="page">
      <header className="page-head"><h1>Agents</h1><button onClick={load} disabled={busy}>Refresh</button></header>
      {err && <p className="err">{err}</p>}

      <div className="toolbar">
        <div className="tabs">
          <button className={`tab ${tab === 'mine' ? 'active' : ''}`} onClick={() => setTab('mine')}>My Agents</button>
          <button className={`tab ${tab === 'templates' ? 'active' : ''}`} onClick={() => setTab('templates')}>Templates</button>
        </div>
        <div className="search-wrap"><input placeholder="Search agents…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <span className="count-pill">{tab === 'mine' ? `${filtered.length} agents` : `${filteredTpl.length} templates`}</span>
      </div>

      {tab === 'mine' ? (
        filtered.length === 0
          ? <div className="empty">No agents{q ? ' match your search' : ' yet'}. Create one below, or browse <b>Templates</b>.</div>
          : <div className="browse-grid">
              {filtered.map((a) => (
                <div key={a} className={`browse-card ${sel === a ? 'active' : ''}`} onClick={() => open(a)}>
                  <div className="bc-title"><span className="ico">⚇</span>{a}</div>
                  <div className="bc-desc">Markdown-defined agent in your namespace.</div>
                  <div className="bc-meta"><span className="badge accent">{conn.provider}</span></div>
                  <div className="bc-actions">
                    <button className="link grow" onClick={(e) => { e.stopPropagation(); open(a); }}>Open</button>
                    <button className="link danger" onClick={(e) => { e.stopPropagation(); remove(a); }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
      ) : (
        <div className="browse-grid">
          {filteredTpl.map((t) => (
            <div key={t.id} className="browse-card">
              <div className="bc-title"><span className="ico">✦</span>{t.title}</div>
              <div className="bc-desc">{t.desc}</div>
              <div className="bc-meta"><span className="badge green">template</span></div>
              <div className="bc-actions">
                <button className="link grow" onClick={() => install(t.requirements, t.id)} disabled={busy}>Install →</button>
                <button className="link" onClick={() => { setReq(t.requirements); setTab('mine'); }}>Edit brief</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'mine' && (
        <div className="panel">
          <div className="panel-head"><b>Create agent</b><span className="muted small">natural language → agent_maker</span></div>
          <textarea rows={2} placeholder="An agent that drafts release notes from merged PRs…" value={req} onChange={(e) => setReq(e.target.value)} />
          <div style={{ marginTop: 8 }}><button onClick={() => req.trim() && install(req)} disabled={busy || !req.trim()}>Create</button></div>
        </div>
      )}

      {sel && (
        <div className="panel">
          <div className="panel-head"><b><code>{sel}</code> · agent.md</b><button onClick={save} disabled={busy}>Save</button></div>
          <textarea className="editor" value={md} onChange={(e) => setMd(e.target.value)} />
          <div className="run">
            <div className="list-head">Run · {conn.provider}{conn.model ? ` / ${conn.model}` : ''}</div>
            <textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
            <button onClick={run} disabled={busy}>Run ▸</button>
            {out && <pre className="out">{out}</pre>}
          </div>
        </div>
      )}
    </div>
  );
}
